/**
 * The 169 hands, and what a chart node says about them.
 *
 * All of this is arithmetic over a `ChartNode.strategy` the server has already sent —
 * the node route returns the whole 169-hand map for a spot the account paid a spot
 * for, so the grid, the range width and the pot odds cost nothing more to show. That
 * is why none of this is behind a capability: gating a picture of numbers the client
 * legitimately holds would be a lock on the door of a room with no walls.
 *
 * Kept out of the components because it is arithmetic and therefore testable. The
 * whole reason the trainer can claim a range width is that this file can be checked
 * against a hand-counted range.
 */

/** High to low. `RANKS` in `cards.ts` runs the other way, which is the wrong order for a grid. */
export const GRID_RANKS = "AKQJT98765432";

/** Every two-card combination there is, before suits are distinguished. */
export const TOTAL_COMBOS = 1326;

export interface HandCell {
  /** "AA", "AKs", "AKo" — the chart's own code. */
  code: string;
  row: number;
  col: number;
  kind: "pair" | "suited" | "offsuit";
}

/**
 * The grid, in reading order.
 *
 * Row and column both run A→2. Above the diagonal is suited and below it is offsuit,
 * which is the convention every solver output and every training site uses; getting it
 * backwards would put AKs where AKo belongs and be invisible to anyone who did not
 * already know the range.
 */
export const HAND_GRID: HandCell[] = (() => {
  const out: HandCell[] = [];
  for (let r = 0; r < 13; r++) {
    for (let c = 0; c < 13; c++) {
      const hi = GRID_RANKS[r]!, lo = GRID_RANKS[c]!;
      if (r === c) out.push({ code: `${hi}${hi}`, row: r, col: c, kind: "pair" });
      else if (c > r) out.push({ code: `${hi}${lo}s`, row: r, col: c, kind: "suited" });
      else out.push({ code: `${lo}${hi}o`, row: r, col: c, kind: "offsuit" });
    }
  }
  return out;
})();

/** Combos of a hand code, weighted correctly: 6 for a pair, 4 suited, 12 offsuit. */
export function combos(code: string): number {
  if (code.length === 2) return 6;
  return code[2] === "s" ? 4 : 12;
}

export interface StrategyLike { freqs: number[]; weight?: number }

/**
 * Which action index is the fold, found by id rather than assumed.
 *
 * Every node this pipeline emits happens to put the aggressive action first and the
 * fold second, and relying on that is exactly the kind of assumption that survives
 * until the day a node has three actions. Returns -1 when there is no fold, which is
 * a real case: a node where folding is not offered has a range width of 100% and the
 * caller has to be able to tell that apart from "no fold found, assume nothing".
 */
export function foldIndex(actions: readonly { id: string }[]): number {
  return actions.findIndex((a) => a.id === "fold");
}

/**
 * How wide the actor plays here, as a fraction of all hands dealt.
 *
 * Weighted by combos, because a range is a share of the hands you are actually dealt
 * and AA is dealt half as often as AKs. Counting the 169 codes evenly would call a
 * range of every pair "7.7% of hands" when it is 5.9%, and a trainer that misreports
 * a range width is worse than one that does not report it.
 */
export function rangeWidth(
  strategy: Record<string, StrategyLike>,
  actions: readonly { id: string }[],
): number | null {
  const fi = foldIndex(actions);
  let played = 0, total = 0;
  for (const code of Object.keys(strategy)) {
    const w = combos(code);
    const f = strategy[code]!.freqs;
    // A hand with no frequency vector says nothing; it must not be counted as folded.
    if (!Array.isArray(f) || f.length === 0) continue;
    total += w;
    const foldFreq = fi < 0 ? 0 : (f[fi] ?? 0);
    played += w * (1 - foldFreq);
  }
  return total > 0 ? played / total : null;
}

/**
 * The price of the call, as the fraction of the final pot being laid.
 *
 * Only meaningful facing a bet. On an opening node there is nothing to call, and
 * returning 0 there would read as "free", so it returns null and the panel says what
 * the node actually is.
 */
export function potOdds(potBb: number, callBb: number): number | null {
  if (!(callBb > 0) || !(potBb > 0)) return null;
  return callBb / (potBb + callBb);
}

/**
 * How often a jam has to take it down to break even on the fold equity alone.
 *
 * Risk over risk-plus-reward — the standard bluff breakeven. It is a floor, not the
 * threshold: the jam also wins some of the time it gets called, so the real bar is
 * lower. Said that way in the copy rather than presented as the answer.
 */
export function breakevenFold(riskBb: number, potBb: number): number | null {
  if (!(riskBb > 0) || !(potBb > 0)) return null;
  return riskBb / (riskBb + potBb);
}

/**
 * The mix for one hand, index-aligned to the node's actions.
 *
 * An absent hand returns null rather than a row of zeros: "the chart does not cover
 * this hand" and "the chart says never" are different statements and the grid colours
 * them differently.
 */
export function mixFor(
  strategy: Record<string, StrategyLike>,
  code: string,
  actionCount: number,
): number[] | null {
  const s = strategy[code];
  if (!s || !Array.isArray(s.freqs) || s.freqs.length === 0) return null;
  const out: number[] = [];
  for (let i = 0; i < actionCount; i++) out.push(s.freqs[i] ?? 0);
  return out;
}

/**
 * How committed a hand is to the non-fold action, 0–1, for colouring one cell.
 *
 * Null propagates: an uncovered hand is drawn as uncovered.
 */
export function playFreq(
  strategy: Record<string, StrategyLike>,
  code: string,
  actions: readonly { id: string }[],
): number | null {
  const s = strategy[code];
  if (!s || !Array.isArray(s.freqs) || s.freqs.length === 0) return null;
  const fi = foldIndex(actions);
  return fi < 0 ? 1 : Math.min(1, Math.max(0, 1 - (s.freqs[fi] ?? 0)));
}

/**
 * The band a cell falls in. Three bands, not a continuous gradient.
 *
 * A gradient looks better and reads worse: the one thing a player needs off this grid
 * is which hands are MIXED, and a smooth ramp hides the boundary between "always" and
 * "nearly always" exactly where it matters. `pure` and `never` are the ends; anything
 * in between is a mix and is drawn as one.
 */
export type Band = "pure" | "mix" | "never" | "uncovered";

export function band(freq: number | null): Band {
  if (freq === null) return "uncovered";
  if (freq >= 0.995) return "pure";
  if (freq <= 0.005) return "never";
  return "mix";
}

/**
 * Round a mix to whole percents that still add to a hundred.
 *
 * Rounding each action on its own printed "All in 51% / Fold 50%" for a 0.505/0.495
 * split — a mix that adds to 101 reads as a bug in the solve rather than in the
 * formatting. Largest remainder: round everything down, then hand the leftover points
 * to whichever actions were closest to rounding up.
 */
export function wholePercents(freqs: readonly number[]): number[] {
  const raw = freqs.map((f) => f * 100);
  const out = raw.map((v) => Math.floor(v));
  let left = Math.round(raw.reduce((a, b) => a + b, 0)) - out.reduce((a, b) => a + b, 0);
  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  for (const { i } of order) { if (left <= 0) break; out[i] = out[i]! + 1; left--; }
  return out;
}
